import React, {
  useCallback,
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import type { FC, ReactNode } from 'react';
import pWaitFor, { TimeoutError } from 'p-wait-for';
import { Spinner, useAsync, useAsyncCallback } from '@wix/cli-ui-kit';
import { useHttpClient } from '@wix/cli-http-client-react';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useErrorReporter } from '@wix/cli-error-reporting';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { PlatformType } from '@wix/cli-app-definitions';
import { useTranslation } from '@wix/cli-i18n';
import type { VariantOf } from 'variant';
import { fields, isType, match, partial, variant } from 'variant';
import { useProjectModel } from '../../model.js';
import {
  redirectWithCSMv2FTs,
  useOpenPage,
  useResolvePlatformDevUrl,
} from '../extensions/utils.js';
import { useSite } from '../../site/useSite.js';
import { useArmTag } from '../../context/armTag.js';
import { usePlatform } from '../Platform/usePlatform.js';
import { useAppComponentsMigrationsContext } from './AppComponentsMigrations.js';
import { useExtensions } from '../extensions/useExtensions.js';

function isAbortError(error: unknown) {
  return (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    error.name === 'AbortError'
  );
}

interface EditorInstallationState {
  openEditor: () => Promise<void>;
}

const EditorInstallationContext = createContext<EditorInstallationState | null>(
  null
);

export function useEditorInstallation(): EditorInstallationState {
  const context = useContext(EditorInstallationContext);
  if (context == null) {
    throw new Error(
      'useEditorInstallation() cannot be used without an <EditorInstallationProvider>'
    );
  }
  return context;
}

const EditorInstallationStatus = variant({
  CheckInstallation: {},
  NotInstalled: fields<{
    installationUrl: string;
    checkInstalledState: CheckAppInstalledState;
  }>(),
  WaitForInstallation: fields<{ installationUrl: string }>(),
  Installed: {},
});

type EditorInstallationStatus = VariantOf<typeof EditorInstallationStatus>;

type CheckAppInstalledState = (wait?: boolean) => Promise<boolean>;

const CheckEditorInstallation: FC<{
  onReady: (cb: CheckAppInstalledState) => void;
}> = ({ onReady }) => {
  const { model } = useProjectModel();
  const { reportError } = useErrorReporter();
  const { site } = useSite();
  const { isAppInstalledInEditor } = useDevCenterClient({ siteId: site.id });

  const checkAppInstalledState = useAsyncCallback(
    async ({ signal }, shouldWait = false): Promise<boolean> => {
      const { appId } = model.config;
      const { pollInterval } = getTestOverrides();

      try {
        if (shouldWait) {
          await pWaitFor(() => isAppInstalledInEditor(appId), {
            // Polling interval is set to 6.5 seconds to avoid rate limiting (429)
            interval: pollInterval ?? 6500,
            timeout: {
              milliseconds: 60 * 1000 * 2,
              signal,
            },
          });

          return true;
        }

        return await isAppInstalledInEditor(appId);
      } catch (error) {
        if (!isAbortError(error) && !(error instanceof TimeoutError)) {
          reportError(
            new CliError({
              cause: error,
              code: CliErrorCode.WaitForEditorInstallationStateFailure(),
            })
          );
        }

        return false;
      }
    }
  );

  useAsync(async () => {
    onReady(checkAppInstalledState.execute);
  }, [site.id]);

  return null;
};

export const EditorInstallation: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const { model } = useProjectModel();
  const httpClient = useHttpClient({ type: 'backoffice' });
  const { isPlatformExists } = usePlatform();
  const { getExtensions } = useExtensions();
  const { site } = useSite();
  const { armTag } = useArmTag();
  const openPage = useOpenPage();
  const { addedSitePresence } = useAppComponentsMigrationsContext();
  const resolvePlatformUrl = useResolvePlatformDevUrl();
  const [editorInstallationStatus, setEditorInstallationStatus] =
    useState<EditorInstallationStatus>(
      EditorInstallationStatus.CheckInstallation()
    );

  const openAddPanel = useMemo(
    () =>
      getExtensions('CustomElement').some(
        (element) =>
          !element.installation?.autoAddToSite &&
          (element.presets?.length ?? 0) > 0
      ),
    [getExtensions]
  );

  const openEditor = useCallback(async () => {
    const platformType = PlatformType.Editor();

    const installationUrl = match(
      editorInstallationStatus,
      partial({
        NotInstalled: ({ installationUrl }) => installationUrl,
        WaitForInstallation: ({ installationUrl }) => installationUrl,
        default: () => resolvePlatformUrl(platformType, openAddPanel),
      })
    );

    await openPage({ title: platformType.type, url: installationUrl });

    if (isType(editorInstallationStatus, 'NotInstalled')) {
      setEditorInstallationStatus(
        EditorInstallationStatus.WaitForInstallation({ installationUrl })
      );
      const isInstalled =
        await editorInstallationStatus.checkInstalledState(true);

      if (isInstalled) {
        setEditorInstallationStatus(EditorInstallationStatus.Installed());
      } else {
        setEditorInstallationStatus(editorInstallationStatus);
      }
    }
  }, [openPage, resolvePlatformUrl, editorInstallationStatus, openAddPanel]);

  const buildInstallationUrl = useCallback(async () => {
    const { data: signedAppMarketParams } = await httpClient
      .post<string>('/_serverless/app-market-json-web-token/json-web-token', {
        data: {
          data: JSON.stringify({
            appDefId: model.config.appId,
            version: 'latest',
          }),
        },
      })
      .catch((e: unknown) => {
        throw new CliError({
          code: CliErrorCode.FailedToGenerateAppMarketQueryParam(),
          cause: e,
        });
      });

    const url = new URL(`https://wix.com/editor/${site.id}`);
    url.searchParams.append('openPanel', 'appMarket');
    url.searchParams.append('preferAppManager', 'true');
    url.searchParams.append('appMarketParams', signedAppMarketParams);
    url.searchParams.append('apps-override', armTag);

    if (openAddPanel) {
      url.searchParams.append('deeplink', `addpanel!${model.config.appId}`);
    }

    return redirectWithCSMv2FTs(url, PlatformType.Editor()).toString();
  }, [httpClient, model.config.appId, site.id, armTag, openAddPanel]);

  const { status, execute } = useAsyncCallback(
    async (_, checkInstalledState: CheckAppInstalledState) => {
      if (isPlatformExists(PlatformType.Editor())) {
        const isInstalled = addedSitePresence
          ? false
          : await checkInstalledState();

        if (!isInstalled) {
          const installationUrl = await buildInstallationUrl();

          return setEditorInstallationStatus(
            EditorInstallationStatus.NotInstalled({
              installationUrl,
              checkInstalledState,
            })
          );
        }
      }

      return setEditorInstallationStatus(EditorInstallationStatus.Installed());
    }
  );

  useEffect(() => {
    setEditorInstallationStatus(EditorInstallationStatus.CheckInstallation());
  }, [site.id]);

  return (
    <EditorInstallationContext.Provider value={{ openEditor }}>
      {!isType(editorInstallationStatus, 'Installed') && (
        <CheckEditorInstallation onReady={execute} />
      )}
      {match(status, {
        NotRequested: () => null,
        Loading: () => <Spinner text={t('general.loading')} />,
        Error: () => null,
        Success: () => children,
      })}
    </EditorInstallationContext.Provider>
  );
};
